import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useLanguage } from '../../hooks/useLanguage';
import { PermanentNumber } from '../../types/api';
import { formatDate } from '../../utils/dateFormatter';

interface PermanentNumberCardProps {
  permanentNumber: PermanentNumber;
}

export function PermanentNumberCard({ permanentNumber }: PermanentNumberCardProps) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-500';
      case 'expired':
        return 'bg-neutral-dark text-neutral-medium';
      case 'refunded':
        return 'bg-blue-500/20 text-blue-500';
      case 'cancelled':
        return 'bg-red-500/20 text-red-500';
      case 'waiting':
        return 'bg-yellow-500/20 text-yellow-500';
      case 'paused':
        return 'bg-yellow-500/20 text-yellow-500';
      default:
        return 'bg-neutral-dark text-neutral-medium';
    }
  };

  const getStatusLabel = (status: string): string => {
    return t(`permanentNumbers.status.${status}`);
  };

  return (
    <Link
      to={`/permanent-numbers/${permanentNumber._id}`}
      className="block bg-neutral-dark/50 backdrop-blur-sm border border-neutral-dark rounded-lg p-4 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-heading font-semibold text-brand-accent mb-1">
              {permanentNumber.number}
            </h3>
            <p className="text-sm text-neutral-medium font-body">
              {permanentNumber.serviceName}
            </p>
          </div>
          <span className={`inline-block px-2 py-1 rounded-full text-xs font-body ${getStatusColor(permanentNumber.status)}`}>
            {getStatusLabel(permanentNumber.status)}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div>
            <p className="text-neutral-medium font-body">
              {t('permanentNumbers.details.country')}:{' '}
              <span className="text-neutral-light">{permanentNumber.countryName}</span>
            </p>
            {(permanentNumber.stateName || permanentNumber.areaCode) && (
              <p className="text-neutral-medium font-body mt-1">
                {t('permanentNumbers.details.state')}:{' '}
                <span className="text-neutral-light">{permanentNumber.stateName || permanentNumber.areaCode}</span>
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-neutral-medium font-body">
              {t('permanentNumbers.details.price')}
            </p>
            <p className="text-lg font-heading font-bold text-brand-accent">
              {permanentNumber.price.toFixed(2)} USD
            </p>
          </div>
        </div>

        <div className="pt-3 border-t border-neutral-dark flex items-center justify-between">
          <div className="text-xs text-neutral-medium font-body">
            {t('permanentNumbers.details.expiresAt')}:{' '}
            {permanentNumber.expiresAt ? formatDate(permanentNumber.expiresAt, 'PP h:mm a', currentLanguage) : '-'}
          </div>
          {permanentNumber.isCodeReceived && (
            <span className="text-xs text-green-500 font-body">
              {t('permanentNumbers.sms.received')}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}


